import React, { useMemo } from 'react';
import { useWeather } from '../../context';
import { LineChart, Line, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer } from 'recharts';

const droughtLevels = {
  no_drought: 'No Drought',
  abnormally_dry: 'Abnormally Dry',
  moderate_drought: 'Moderate Drought',
  severe_drought: 'Severe Drought',
  extreme_drought: 'Extreme Drought',
  exceptional_drought: 'Exceptional Drought'
};

const droughtColors = {
  no_drought: '#00ff00',
  abnormally_dry: '#ffff00',
  moderate_drought: '#ffa500',
  severe_drought: '#ff4500',
  extreme_drought: '#ff0000',
  exceptional_drought: '#800000'
};

export function DroughtAnalytics() {
  const { weatherData, selectedState, startDate, endDate } = useWeather();

  const processedData = useMemo(() => {
    if (!weatherData || !selectedState || !weatherData[selectedState]?.all) {
      return { timeSeriesData: [], currentStats: null, trends: null };
    }

    const stateData = weatherData[selectedState].all;
    const dates = Object.keys(stateData)
      .filter(date => {
        const reportDate = new Date(stateData[date].date);
        const start = startDate ? new Date(startDate) : new Date(0);
        const end = endDate ? new Date(endDate) : new Date();
        return reportDate >= start && reportDate <= end;
      })
      .sort();
    
    if (dates.length === 0) {
      return { timeSeriesData: [], currentStats: null, trends: null };
    }

    // Process time series data
    const timeSeriesData = dates.map(date => ({
      date: new Date(stateData[date].date).toLocaleDateString(),
      ...stateData[date].drought_levels,
      total: stateData[date].total_drought_percentage
    }));

    // Get current stats (most recent date)
    const currentStats = stateData[dates[dates.length - 1]];

    // Calculate trends (compare with 4 weeks ago)
    const fourWeeksAgo = dates[Math.max(0, dates.length - 5)];
    const trends = {
      total: currentStats.total_drought_percentage - stateData[fourWeeksAgo].total_drought_percentage,
      levels: Object.entries(currentStats.drought_levels).reduce((acc, [key, value]) => ({
        ...acc,
        [key]: value - (stateData[fourWeeksAgo].drought_levels[key] || 0)
      }), {})
    };

    return { timeSeriesData, currentStats, trends };
  }, [weatherData, selectedState, startDate, endDate]);

  if (!selectedState) {
    return (
      <div className="glass-panel m-4 p-8">
        <div className="text-center text-[var(--secondary-text)]">
          Select a state to view drought analytics
        </div>
      </div>
    );
  }

  if (!processedData.currentStats) {
    return (
      <div className="glass-panel m-4 p-8">
        <div className="text-center text-[var(--secondary-text)]">
          No drought data available for the selected date range
        </div>
      </div>
    );
  }

  const { timeSeriesData, currentStats, trends } = processedData;

  return (
    <div className="space-y-6 glass-panel m-4 p-6">
      {/* Current Status */}
      <div>
        <h3 className="text-lg font-semibold mb-4">Current Drought Status</h3>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
          {Object.entries(currentStats.drought_levels).map(([level, percentage]) => (
            <div key={level} className="glass-panel p-4">
              <h4 className="text-sm text-[var(--secondary-text)]">{droughtLevels[level]}</h4>
              <div className="flex items-end gap-2">
                <span className="text-2xl font-bold" style={{ color: droughtColors[level] }}>
                  {percentage.toFixed(1)}%
                </span>
                {trends.levels[level] !== 0 && (
                  <span className={`text-sm ${trends.levels[level] > 0 ? 'text-[var(--danger-color)]' : 'text-[var(--success-color)]'}`}>
                    {trends.levels[level] > 0 ? '+' : ''}{trends.levels[level].toFixed(1)}%
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Time Series Chart */}
      <div>
        <h3 className="text-lg font-semibold mb-4">Drought Level Trends</h3>
        <div className="h-[400px]">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={timeSeriesData}>
              <XAxis 
                dataKey="date" 
                stroke="var(--secondary-text)"
                tick={{ fill: 'var(--secondary-text)' }}
              />
              <YAxis 
                stroke="var(--secondary-text)"
                tick={{ fill: 'var(--secondary-text)' }}
                label={{ 
                  value: 'Coverage (%)', 
                  angle: -90, 
                  position: 'insideLeft',
                  fill: 'var(--secondary-text)'
                }}
              />
              <Tooltip
                contentStyle={{
                  background: 'var(--secondary-bg)',
                  border: '1px solid var(--border-color)',
                  color: 'var(--primary-text)'
                }}
              />
              <Legend />
              {Object.entries(droughtLevels).map(([key, label]) => (
                <Line
                  key={key}
                  type="monotone"
                  dataKey={key}
                  name={label}
                  stroke={droughtColors[key]}
                  strokeWidth={2}
                  dot={false}
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Additional Stats */}
      <div>
        <h3 className="text-lg font-semibold mb-4">Report Details</h3>
        <div className="grid grid-cols-2 gap-4">
          <div className="glass-panel p-4">
            <h4 className="text-sm text-[var(--secondary-text)]">Valid Period</h4>
            <p className="text-sm">
              {new Date(currentStats.valid_start).toLocaleDateString()} to{' '}
              {new Date(currentStats.valid_end).toLocaleDateString()}
            </p>
          </div>
          <div className="glass-panel p-4">
            <h4 className="text-sm text-[var(--secondary-text)]">Total Drought Impact</h4>
            <div className="flex items-end gap-2">
              <span className="text-xl font-bold">{currentStats.total_drought_percentage.toFixed(1)}%</span>
              {trends.total !== 0 && (
                <span className={`text-sm ${trends.total > 0 ? 'text-[var(--danger-color)]' : 'text-[var(--success-color)]'}`}>
                  {trends.total > 0 ? '+' : ''}{trends.total.toFixed(1)}%
                </span>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}